import type { TriageEvidence } from "@/lib/api";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";

const link = css({
  color: "text.accent.blue",
  textDecoration: "underline",
  textDecorationColor: "border",
  textUnderlineOffset: "[2px]",
  outline: "none",
  _hover: { textDecorationColor: "text.accent.blue" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
});

const KIND: Record<TriageEvidence["kind"], string> = {
  pr: "PR",
  issue: "issue",
  commit: "commit",
  file: "file",
  email: "email",
  calendar: "calendar",
  note: "note",
  page: "page",
};

function stateColor(state?: string): string {
  if (!state) return "text.subtlest";
  if (state.startsWith("merged") || state.startsWith("closed") || state === "exists")
    return "text.success";
  if (state.startsWith("open")) return "text.warning";
  return "text.subtle";
}

/** Evidence Hal found for a label: what it is, its state, and a link when there is one. */
export function EvidenceList({ items }: { items: TriageEvidence[] }) {
  if (items.length === 0) {
    return (
      <Text textStyle="mono.xs" color="text.subtlest">
        No evidence attached.
      </Text>
    );
  }
  return (
    <Box as="ul" display="flex" flexDir="column" gap="4" listStyle="none" p="0" m="0">
      {items.map((e) => (
        <Box
          as="li"
          key={`${e.kind}:${e.ref}`}
          display="flex"
          gap="8"
          alignItems="baseline"
          flexWrap="wrap"
        >
          <Text textStyle="mono.xs" color="text.subtlest" minW="[52px]">
            {KIND[e.kind]}
          </Text>
          {e.url ? (
            <a href={e.url} target="_blank" rel="noreferrer" className={link}>
              <Text as="span" textStyle="mono.xs">
                {e.ref}
              </Text>
            </a>
          ) : (
            <Text textStyle="mono.xs" color="text">
              {e.ref}
            </Text>
          )}
          {e.state && (
            <Text textStyle="mono.xs" color={stateColor(e.state)}>
              {e.state}
            </Text>
          )}
          {e.title && (
            <Text fontSize="12" color="text.subtle">
              {e.title}
            </Text>
          )}
        </Box>
      ))}
    </Box>
  );
}
